import { useState } from "react";
import { Box, Typography, Paper, Stack, Radio, RadioGroup, FormControlLabel, Button, Divider, CircularProgress, Alert } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import dayjs from "dayjs";
import ConfirmationModal from "../../../../components/confirmation-modal";
import RichEditor from "../../../../components/rich-editor";
import { generateButtonSx } from "../../../../utils/elementor-style-override";
import useAgentPreferenceApprovals from "./hooks/useAgentPreferenceApprovals";

const primaryBlue = "#1D54A2";
const EMPTY_CONTENT = "<p></p>";

const approveButtonSx = {
	backgroundColor: "#1E7E4D !important",
	color: "#FFFFFF !important",
	fontWeight: 700,
	...generateButtonSx({ bg: "#16663D !important", text: "#FFFFFF" }, { bg: "#16663D !important", text: "#FFFFFF" }),
};

const rejectButtonSx = {
	color: "#B93544 !important",
	backgroundColor: "#FFF0F2 !important",
	fontWeight: 700,
	...generateButtonSx({ bg: "#FBD9DE !important", text: "#B93544" }, { bg: "#FBD9DE !important", text: "#B93544" }),
};

const readOnlyRadioSx = {
	color: "#B9C7DC",
	"&.Mui-checked": { color: primaryBlue },
	"&.Mui-disabled.Mui-checked": { color: primaryBlue },
	"&.Mui-disabled": { color: "#B9C7DC" },
};

const AgentPreferenceApprovals = () => {
	const { pending, loading, error, review } = useAgentPreferenceApprovals();
	const [pendingAction, setPendingAction] = useState(null); // { submissionId, agentName, action: "approve" | "reject" }

	const requestAction = (submissionId, agentName, action) => {
		setPendingAction({ submissionId, agentName, action });
	};

	const confirmAction = async () => {
		if (!pendingAction) return;
		await review(pendingAction.submissionId, pendingAction.action === "approve" ? "Approved" : "Rejected");
		setPendingAction(null);
	};

	return (
		<Box mt={5}>
			<Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
				<HourglassEmptyIcon fontSize="small" sx={{ color: primaryBlue }} />
				<Typography variant="h5" fontWeight={600}>
					Pending Approvals
				</Typography>
			</Stack>
			<Typography variant="body2" color="text.secondary" mb={3}>
				Preference changes agents have submitted, waiting for your review.
			</Typography>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<CircularProgress size={26} sx={{ color: primaryBlue }} />
				</Box>
			) : pending.length === 0 ? (
				<Paper variant="outlined" sx={{ borderRadius: 2, p: 3, textAlign: "center" }}>
					<Typography variant="body2" color="text.secondary">
						No pending preference changes.
					</Typography>
				</Paper>
			) : (
				<Stack spacing={2}>
					{pending.map((submission) => {
						const agentName = submission.agent?.name || `Agent #${submission.agent?.id}`;

						return (
							<Paper key={submission.id} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
								<Stack
									direction={{ xs: "column", sm: "row" }}
									alignItems={{ sm: "center" }}
									justifyContent="space-between"
									spacing={1}
									px={2}
									py={1.5}
									sx={{ backgroundColor: "secondary.lightbgcolor" }}
								>
									<Box>
										<Typography variant="subtitle2" fontWeight={700}>
											{agentName}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											Submitted {dayjs(submission.submitted_at).format("MMMM D, YYYY [at] h:mm A")}
										</Typography>
									</Box>
									<Stack direction="row" spacing={1}>
										<Button
											size="small"
											variant="contained"
											startIcon={<CheckCircleOutlineIcon />}
											onClick={() => requestAction(submission.id, agentName, "approve")}
											sx={approveButtonSx}
										>
											Approve
										</Button>
										<Button
											size="small"
											variant="contained"
											startIcon={<HighlightOffIcon />}
											onClick={() => requestAction(submission.id, agentName, "reject")}
											sx={rejectButtonSx}
										>
											Reject
										</Button>
									</Stack>
								</Stack>
								<Divider />
								<Box p={2}>
									<Stack spacing={1} mb={2.5}>
										{(submission.answers || []).map((answer) => (
											<Box
												key={answer.catalog_id ?? answer.label}
												sx={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", columnGap: 2 }}
											>
												<Typography variant="body2" fontWeight={600}>
													{answer.label}
												</Typography>
												<RadioGroup row value={answer.answer ? "yes" : "no"} sx={{ flexWrap: "nowrap" }}>
													<FormControlLabel
														value="yes"
														disabled
														control={<Radio size="small" sx={readOnlyRadioSx} />}
														label="Yes"
														sx={{ mr: 1, whiteSpace: "nowrap" }}
													/>
													<FormControlLabel
														value="no"
														disabled
														control={<Radio size="small" sx={readOnlyRadioSx} />}
														label="No"
														sx={{ mr: 0, whiteSpace: "nowrap" }}
													/>
												</RadioGroup>
											</Box>
										))}
									</Stack>
									<Typography variant="subtitle2" fontWeight={600} mb={1}>
										Remarks
									</Typography>
									<RichEditor value={submission.remarks || EMPTY_CONTENT} readOnly />
								</Box>
							</Paper>
						);
					})}
				</Stack>
			)}

			<ConfirmationModal
				open={!!pendingAction}
				onClose={() => setPendingAction(null)}
				onConfirm={confirmAction}
				title={pendingAction?.action === "approve" ? "Approve Preference Change?" : "Reject Preference Change?"}
				description={
					pendingAction
						? pendingAction.action === "approve"
							? `Approve ${pendingAction.agentName}'s submitted preferences?`
							: `Reject ${pendingAction.agentName}'s submitted preferences?`
						: ""
				}
				confirmText={pendingAction?.action === "approve" ? "Yes, Approve" : "Yes, Reject"}
				cancelText="Cancel"
				confirmColor={pendingAction?.action === "approve" ? "#1E7E4D" : "#B93544"}
			/>
		</Box>
	);
};

export default AgentPreferenceApprovals;
